<template>
    <div class="card card-body shadow-sm mt-0">
      <div class="form-header d-flex justify-content-between align-items-center">
        <h4 class="form-title">
          {{ route.params.id ? 'Editar Modelo de Contrato' : 'Novo Modelo de Contrato' }}
        </h4>
        <button class="btn btn-secondary btn-sm me-3" @click="voltar">Voltar</button>
      </div>
  
      <BaseForm
        :isFormValid="isFormValid"
        :isSubmitting="isSubmitting"
        @submit="salvarModeloContrato"
        @cancel="voltar"
      >
        <!-- Informações Básicas do Modelo -->
        <FormSection title="Informações do Modelo">
          <div class="d-flex align-items-center" style="gap: 1.5rem;">
            <!-- Título -->
            <InputField
              v-model="modeloContrato.titulo"
              label="Título do Contrato <span class='required'>*</span>"
              id="titulo"
              placeholder="Digite o título do contrato"
              required
              width="400px"
              :readonly="isLocked"
            />

            <!-- Status -->
            <div class="form-group">
              <label for="em_uso">Status</label>
              <div class="form-switch">
                <input
                  type="checkbox"
                  id="em_uso"
                  v-model="modeloContrato.em_uso"
                  class="form-check-input"
                  :disabled="isLocked"
                />
                <label for="em_uso" class="form-check-label">
                  {{ modeloContrato.em_uso 
                      ? "Em uso pelo sistema (não pode ser excluido)"
                      : "Ainda não utilizado pelo sistema" }}
                </label>
              </div>
            </div>
          </div>
        </FormSection>
  
        <!-- Conteúdo do Contrato -->
        <FormSection title="Conteúdo do Contrato">
          <!-- Utilize aqui seu editor de texto rico para criar/editar o conteúdo com formatação -->
          <RichTextEditor
            v-model="modeloContrato.conteudo"
            label="Conteúdo do Contrato"
            id="conteudo"
            placeholder="Edite o conteúdo do contrato com formatação..."
          />
        </FormSection>

        <!-- ✅ Anexo PDF (somente Contrato de Matrícula - Opção A) -->
        <FormSection
          v-if="isContratoMatricula"
          title="Anexo do Contrato (PDF)"
        >
          <div class="row g-3">
            <div class="col-12">
              <label class="form-label">PDF Anexo (será incluído no final do contrato)</label>

              <div v-if="existingAnexo || anexoFile" class="d-flex align-items-center">
                <input
                  type="text"
                  class="form-control"
                  :value="anexoFile ? anexoFile.name : existingAnexo.name"
                  disabled
                />
                <i
                  class="bi bi-trash-fill text-danger ms-2"
                  role="button"
                  title="Remover anexo"
                  @click="removeAnexo"
                ></i>
              </div>

              <div v-else>
                <input
                  type="file"
                  class="form-control"
                  accept=".pdf"
                  @change="onAnexoChange"
                />
                <small class="text-muted d-block mt-1">Somente PDF.</small>
              </div>

              <div v-if="existingAnexo?.url" class="mt-2">
                <a :href="apiUrl + existingAnexo.url" target="_blank" rel="noopener" class="file-link">
                  Abrir anexo atual
                </a>
              </div>
            </div>
          </div>
        </FormSection>

      </BaseForm>
    </div>
  </template>
  
  <script>
  import { reactive, ref, computed, onMounted } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import InputField from '@/components/Layout/InputField.vue';
  import FormSection from '@/components/Layout/FormSection.vue';
  import BaseForm from '@/components/Layout/BaseForm.vue';
  import RichTextEditor from '@/components/Layout/RichTextEditor.vue'; // Componente do editor de texto rico
  import axios from '@/services/axios';
  
  export default {
    components: { InputField, FormSection, BaseForm, RichTextEditor },
    setup() {
      const route = useRoute();
      const router = useRouter();
      const apiUrl = process.env.VUE_APP_API_URL;

      const modeloContrato = reactive({
        id: null,
        titulo: '',
        conteudo: '',
        em_uso: false,
        anexo_arquivo_nome: null,
        anexo_caminho: null
      });
  
      const isSubmitting = ref(false);
      const existingAnexo = ref(null);
      const anexoFile = ref(null);
      const shouldRemoveAnexo = ref(false);

      const isContratoMatricula = computed(() =>
        String(modeloContrato.titulo || '').trim() === 'Contrato de Matrícula'
      );


      const isFormValid = computed(() => {
        return modeloContrato.titulo.trim() !== '' && modeloContrato.conteudo.trim() !== '';
      });
  
      const novoModeloContrato = () => {
        isSubmitting.value = false;
        Object.assign(modeloContrato, {
          id: null,
          titulo: '',
          conteudo: '',
          em_uso: false,
        });
      };
  
      // Computed para saber se estamos em edição
      const isEditMode = computed(() => Boolean(route.params.id));

      // Computed que indica se deve desabilitar campos
      const isLocked = computed(() =>
        isEditMode.value && modeloContrato.em_uso
      );

      function onAnexoChange(e) {
        anexoFile.value = e.target.files?.[0] || null;
        if (anexoFile.value) {
          shouldRemoveAnexo.value = false;
        }
      }
      function removeAnexo() {
        anexoFile.value = null;
        existingAnexo.value = null;
        shouldRemoveAnexo.value = true;
      }

      async function uploadAnexoPDF(documentoId) {
        const fd = new FormData();
        fd.append('anexo', anexoFile.value);
        await axios.post(`/configuracoes/documentos/${documentoId}/anexo`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      async function deleteAnexoPDF(documentoId) {
        await axios.delete(`/configuracoes/documentos/${documentoId}/anexo`);
      }


      const salvarModeloContrato = async () => {
        if (isSubmitting.value) return;
        if (!isFormValid.value) {
          console.error("Formulário inválido");
          return;
        }
        isSubmitting.value = true;
        try {
          const method = modeloContrato.id ? "put" : "post";
          const url = modeloContrato.id
            ? `/configuracoes/documentos/${modeloContrato.id}`
            : "/configuracoes/documentos";
  
          const resp = await axios[method](url, {
            titulo: modeloContrato.titulo,
            conteudo: modeloContrato.conteudo,
            em_uso:   modeloContrato.em_uso,
          });
  
          // id pode vir no retorno
          const saved = resp?.data || {};
          const documentoId = modeloContrato.id || saved.id;
          if (documentoId) modeloContrato.id = documentoId;

          // ✅ regra Opção A: só mexe com anexo se for Contrato de Matrícula
          if (isContratoMatricula.value) {
            // 1) remover anexo (se solicitado)
            if (shouldRemoveAnexo.value) {
              await deleteAnexoPDF(documentoId);
              shouldRemoveAnexo.value = false;
            }
            // 2) upload/replace
            if (anexoFile.value) {
              await uploadAnexoPDF(documentoId);
              anexoFile.value = null;
            }
          }


          router.push("/configuracoes/documentos");
        } catch (error) {
          console.error("Erro ao salvar o modelo de contrato:", error);
        } finally {
          isSubmitting.value = false;
        }
      };
  
      const voltar = () => {
        router.push("/configuracoes/documentos");
      };
  
      onMounted(async () => {
        const contratoId = route.params.id;
        if (contratoId) {
          try {
            const response = await axios.get(`/configuracoes/documentos/${contratoId}`);
            Object.assign(modeloContrato, response.data);

            // carrega anexo existente (se houver)
            if (response.data?.anexo_arquivo_nome && response.data?.anexo_caminho) {
              existingAnexo.value = {
                name: response.data.anexo_arquivo_nome,
                url: response.data.anexo_caminho
              };
            } else {
              existingAnexo.value = null;
            }

          } catch (error) {
            console.error("Erro ao carregar modelo de contrato:", error);
          }
        } else {
          novoModeloContrato();
        }
      });
  
      return {
        modeloContrato,
        isFormValid,
        isSubmitting,
        isLocked,
        isEditMode,
        apiUrl,
        isContratoMatricula,
        existingAnexo,
        anexoFile,
        onAnexoChange,
        removeAnexo,
        salvarModeloContrato,
        voltar,
        route,
      };
    },
  };
  </script>
  
  <style scoped>
  .card {
    max-width: 90%;
    margin: 0 auto;
  }
  
  .error-input {
    border-color: #dc3545;
  }
  
  .error-message {
    color: #dc3545;
    font-size: 0.875rem;
  }
  
  .form-switch {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .form-switch {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

  </style>
  